<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <div
      data-testid="sensitive-data-scan-modal-warning"
      class="warning-block px-3 py-2 mb-6 d-flex align-start"
    >
      <v-icon size="24" icon="$warning" class="mt-1 mr-2 flex-shrink-0" />
      <span class="body2" v-html="$t(`modals.${config.action}.scanWarning`)" />
    </div>
    <v-select
      v-model="localValue.item.osType"
      :items="operationSystems"
      density="comfortable"
      class="mb-10 w-30"
      hide-details
      :disabled="config.action === SensitiveDataScansActions.EDIT"
      item-value="value"
      item-title="label"
      :label="$t(`modals.${config.action}.selectOs`)"
      data-testid="sensitive-data-scan-modal-os-select"
    >
    </v-select>
    <div class="subtitle1 mb-4 mt-6">
      {{ $t(`modals.${config.action}.selectScanFrequencyAndTime`) }}
    </div>
    <div class="d-flex mb-10">
      <v-select
        v-model="localValue.item.daysCount"
        :items="days"
        density="comfortable"
        class="mr-6 period-select"
        hide-details
        :label="$t(`modals.${config.action}.every`)"
        data-testid="sensitive-data-scan-modal-frequency-select"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :title="$t('general.everyXDays', { n: item.value }, item.value)"
          />
        </template>
        <template v-slot:selection="{ item }">
          {{ $t("general.everyXDays", { n: item.value }, item.value) }}
        </template>
      </v-select>
      <v-select
        v-model="localValue.item.preferredHour"
        :items="timeValues"
        density="comfortable"
        class="period-select"
        hide-details
        item-title="displayValue"
        item-value="value"
        :label="$t(`modals.${config.action}.preferredTime`)"
      >
      </v-select>
    </div>
    <div class="subtitle1">{{ $t(`modals.${config.action}.applyTo`) }}</div>
    <v-radio-group v-model="localValue.item.appliedDriveType" hide-details class="mt-4">
      <v-radio
        v-for="driveType in driveTypes"
        :key="driveType"
        :value="driveType"
        class="mb-4"
        :ripple="false"
        :label="$t(`modals.${config.action}.driveTypes.${driveType}`)"
      ></v-radio>
    </v-radio-group>
    <v-autocomplete
      v-model="localValue.item.labels"
      :items="labels"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      class="mt-3"
      :label="localValue.item.labels.length ? $t(`modals.${config.action}.labelsLabel`) : ''"
      :placeholder="$t(`modals.${config.action}.labelsPlaceholder`)"
      item-title="name"
      return-object
      chips
      multiple
      closable-chips
      clear-on-select
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.raw.name"
          class="label-chip"
          :color="item.raw.color"
        >
          <div class="d-flex align-center">
            <span class="ml-1 mr-2 body2" v-text="item.title" />
          </div>
        </v-chip>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.name">
          <template v-slot:prepend>
            <coro-label :background-color="item.raw.color" variant="square" class="mr-2">
            </coro-label>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import { OsType } from "@/constants/devices";
import moment from "moment/moment";
import CoroLabel from "@/components/CoroLabel.vue";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { SensitiveDataScanItem } from "@/_store/endpoint-data-governance/sensitive-data-scans.module";
import camelCase from "lodash/camelCase";
import {
  SENSITIVE_DATA_SCAN_FREQUENCY_OPTIONS,
  SensitiveDataScanDriveTypes,
  SensitiveDataScansActions,
} from "@/constants/endpoint-data-governance";
import { atLeastOneIsRequired } from "@/_helpers/validators";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";

export default defineComponent({
  components: { CoroLabel },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();
    const operationSystems = [
      { value: OsType.WINDOWS, label: i18n.t(`general.osVersion.${camelCase(OsType.WINDOWS)}`) },
      { value: OsType.MAC_OS, label: i18n.t(`general.osVersion.${camelCase(OsType.MAC_OS)}`) },
    ];
    const driveTypes = [SensitiveDataScanDriveTypes.ALL, SensitiveDataScanDriveTypes.UNENCRYPTED];
    const localValue = ref<DialogDataConfig<SensitiveDataScanItem>>({
      item: cloneDeep(props.config.item),
      action: props.config.action,
    });
    const valid = ref(true);
    const form = ref();
    const labels = ref<Array<DeviceLabel>>([]);
    const required = reactive([(v: string) => (!v ? i18n.t("validations.required") : true)]);
    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };
    const timeValues = computed(() => {
      return Array.from(
        {
          length: 24,
        },
        (_, hour) => {
          return {
            value: hour,
            displayValue: moment({
              hour,
              minutes: 0,
            }).format("hh:mm A"),
          };
        }
      );
    });

    const getLabels = async (searchParam: string = "") => {
      const { data } = await devicesSettingsStore.searchLabels(searchParam);
      labels.value = data;
    };

    const initialDaysCount = localValue.value.item.daysCount;
    const days = SENSITIVE_DATA_SCAN_FREQUENCY_OPTIONS.includes(initialDaysCount)
      ? SENSITIVE_DATA_SCAN_FREQUENCY_OPTIONS
      : [...SENSITIVE_DATA_SCAN_FREQUENCY_OPTIONS, initialDaysCount].sort((a, b) => b - a);

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal) => {
        if (newVal === null) return;
        emit("update:valid", newVal);
      },
      { deep: true, immediate: false }
    );

    onMounted(async () => {
      await getLabels();
    });

    return {
      camelCase,
      localValue,
      getLabels,
      timeValues,
      driveTypes,
      labels,
      days,
      valid,
      form,
      required,
      validate,
      operationSystems,
      atLeastOneIsRequired,
      SensitiveDataScansActions,
    };
  },
});
</script>

<style lang="scss" scoped>
.period-select {
  width: 172px;
}
</style>
